"use client"

import styled from "@emotion/styled"
import Image from "next/image"
import { useEffect, useRef, useState } from "react"
import { extractProperty, getUmbracoMediaUrl } from "utils"

type Props = {
  data: any
  locale?: string
}

const MAX_ITEMS = 6

// Fire once when the element scrolls into view (entrance animation trigger).
function useInViewOnce<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [inView, setInView] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || inView) return
    const o = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true)
          o.disconnect()
        }
      },
      { threshold: 0.2 },
    )
    o.observe(el)
    return () => o.disconnect()
  }, [inView])
  return { ref, inView }
}

// --- Styled Components ---

const Section = styled.section`
  display: flex;
  justify-content: center;
  padding: 90px 120px;
  background-color: var(--comp-bg);
  color: var(--comp-text);

  ${(props) => props.theme.breakpoints.down("lg")} {
    padding: 60px 40px;
  }

  ${(props) => props.theme.breakpoints.down("md")} {
    padding: 40px 20px;
  }
`

const ItemsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: stretch;
  gap: 48px 64px;
  max-width: 1200px;
  width: 100%;

  ${(props) => props.theme.breakpoints.down("lg")} {
    gap: 40px 32px;
  }

  ${(props) => props.theme.breakpoints.down("md")} {
    gap: 32px 16px;
  }
`

const Item = styled.div<{ $in: boolean; $index: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  flex: 0 1 calc((100% - 128px) / 3);

  /* Slide-down-from-top entrance (ASH-1399): fires once when section enters view. */
  transform: translateY(${(props) => (props.$in ? "0" : "-24px")});
  opacity: ${(props) => (props.$in ? 1 : 0)};
  transition: transform 0.5s ease, opacity 0.5s ease;
  transition-delay: ${(props) => props.$index * 0.08}s;

  ${(props) => props.theme.breakpoints.down("lg")} {
    flex-basis: calc((100% - 64px) / 3);
  }

  ${(props) => props.theme.breakpoints.down("md")} {
    flex-basis: calc((100% - 16px) / 2);
  }
`

/* NOTE: theme-tinting icons via CSS mask requires CORS headers on CMS /media/
   (not enabled today), so icons render as-is and their color is baked into the
   uploaded SVG file. */
const IconWrapper = styled.div`
  position: relative;
  width: 62px;
  height: 62px;
  flex-shrink: 0;
`

const ItemTitle = styled.h3`
  font-size: 24px;
  font-weight: 500;
  line-height: 1.2;
  color: var(--inner-heading, var(--comp-heading));
  margin: 16px 0 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    /* Figma desktop title is 28px (ASH-1430) */
    font-size: 28px;
  }
`

const TitleLine = styled.span`
  display: block;
`

const ItemText = styled.p`
  font-size: 16px;
  font-weight: 700;
  line-height: 1.4;
  color: var(--inner-text, var(--comp-text));
  margin: 8px 0 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 18px;
  }
`

// --- Main Component ---

type BenefitsItem = {
  iconUrl: string
  iconAlt: string
  topLineTitle: string
  bottomLineTitle: string
  text: string
}

function parseItem(block: any): BenefitsItem {
  const icon = extractProperty(block, "icon", true) as any[] | undefined
  const iconAltText = extractProperty(block, "altText") as string | ""
  return {
    iconUrl: icon?.[0]?.url || "",
    iconAlt: iconAltText || icon?.[0]?.altText || "",
    topLineTitle: (extractProperty(block, "topLineTitle") as string) || "",
    bottomLineTitle: (extractProperty(block, "bottomLineTitle") as string) || "",
    text: (extractProperty(block, "text") as string) || "",
  }
}

function isEmptyItem(item: BenefitsItem): boolean {
  return !item.iconUrl && !item.topLineTitle && !item.bottomLineTitle && !item.text
}

function BenefitsIcons({ data }: Props) {
  const itemBlocks = extractProperty(data, "items") as any[] | ""
  const items = (Array.isArray(itemBlocks) ? itemBlocks : [])
    .map(parseItem)
    .filter((item) => !isEmptyItem(item))
    .slice(0, MAX_ITEMS)

  const { ref, inView } = useInViewOnce<HTMLElement>()

  if (items.length === 0) return null

  return (
    <Section ref={ref}>
      <ItemsGrid role="list">
        {items.map((item, i) => (
          <Item key={i} $in={inView} $index={i} role="listitem">
            {item.iconUrl && (
              <IconWrapper>
                <Image
                  src={getUmbracoMediaUrl(item.iconUrl)}
                  fill
                  alt={item.iconAlt}
                  style={{ objectFit: "contain" }}
                />
              </IconWrapper>
            )}
            {(item.topLineTitle || item.bottomLineTitle) && (
              <ItemTitle>
                {item.topLineTitle && <TitleLine>{item.topLineTitle}</TitleLine>}
                {item.bottomLineTitle && <TitleLine>{item.bottomLineTitle}</TitleLine>}
              </ItemTitle>
            )}
            {item.text && <ItemText>{item.text}</ItemText>}
          </Item>
        ))}
      </ItemsGrid>
    </Section>
  )
}

export default BenefitsIcons
